import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { BackToSystemButton } from "@/features/admin/components"
import { useImpersonation } from "@/features/admin/hooks/use-impersonation"
import { useEntitlement } from "@/features/billing/hooks/use-entitlement"
import { ArrowLeft, Home } from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router-dom"

const getPageTitle = (pathname: string): string => {
  // Simplified mapping, can be expanded based on actual routes
  if (pathname.startsWith("/admin")) return "Administração"
  if (pathname.startsWith("/dashboard")) return "Dashboard"
  if (pathname.startsWith("/agenda")) return "Agenda"
  if (pathname.startsWith("/agents")) return "Agentes"
  if (pathname.startsWith("/availability")) return "Disponibilidade"
  if (pathname.startsWith("/contacts")) return "Contatos"
  if (pathname.startsWith("/chat")) return "Conversas"
  if (pathname.startsWith("/reports")) return "Relatórios"
  if (pathname.startsWith("/settings")) return "Configurações"
  if (pathname.startsWith("/calendar")) return "Agendamentos"
  // Add more mappings as needed based on your product-definition
  // e.g., /onboarding, /credits, etc.
  return "Speeke" // Fallback title
}

export function AppHeader() {
  const location = useLocation()
  const navigate = useNavigate()
  const pageTitle = getPageTitle(location.pathname)
  const isAdminContext = location.pathname.startsWith("/admin")
  const isDashboard = location.pathname.startsWith("/dashboard")
  const { isActive, isExpired } = useEntitlement()
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation()

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
      <div className="flex w-full items-center justify-between gap-1 px-4 lg:gap-2 lg:px-6">
        <div className="flex items-center gap-1 lg:gap-2">
          <SidebarTrigger className="-ml-1" />
          {!isDashboard && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 md:hidden"
              onClick={() => navigate("/dashboard")}
            >
              <Home className="h-4 w-4" />
              <span className="sr-only">Ir para Dashboard</span>
            </Button>
          )}
          <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />
          <h1 className="text-base font-medium">{pageTitle}</h1>
        </div>

        <div className="flex items-center gap-2">
          {isImpersonating && impersonatedUser && (
            <>
              <Badge variant="outline" className="border-amber-500 bg-amber-50 text-amber-700">
                Acessando como {impersonatedUser.name}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={stopImpersonation}
                className="h-7 gap-1.5 border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar para Admin
              </Button>
            </>
          )}

          {isActive && (
            <Badge
              variant="outline"
              className="cursor-pointer border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              asChild
            >
              <Link to="/settings/billing">Assinatura Ativa</Link>
            </Badge>
          )}

          {isExpired && (
            <Badge
              variant="outline"
              className="cursor-pointer border-red-500 bg-red-50 text-red-700 hover:bg-red-100"
              asChild
            >
              <Link to="/settings/billing">Assinatura Expirada</Link>
            </Badge>
          )}

          {isAdminContext && <BackToSystemButton />}
        </div>
      </div>
    </header>
  )
}
